<template>
  <v-container fluid>
    <v-card>
      <v-card-title>Аналитика активности пользователей</v-card-title>

      <v-tabs v-model="tab">
        <v-tab>Обзор</v-tab>
        <v-tab>Лента событий</v-tab>
      </v-tabs>

      <!-- Фильтры вкладок; v-show — чтобы обе формы всегда были в DOM (нужно для перехода из таблицы клиентов) -->
      <v-card-text>
        <v-form v-show="tab === TAB_OVERVIEW" ref="overviewForm" lazy-validation @submit.prevent="loadOverview">
          <v-row>
            <v-col cols="12" sm="6" md="4" lg="3">
              <PeriodRangeSelector v-model="overview.period" :max-days="MAX_PERIOD_DAYS" />
            </v-col>

            <v-col cols="12" sm="6" md="8" lg="9" class="d-flex justify-end align-center">
              <v-btn color="success" type="submit" :loading="overview.loading" :disabled="overview.loading">
                Показать
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-form v-show="tab === TAB_FEED" ref="feedForm" lazy-validation @submit.prevent="loadFeed">
          <v-row>
            <v-col cols="12" sm="6" md="4" lg="3">
              <UserSelector v-model="feed.user" single label="Пользователь" />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="3">
              <PeriodRangeSelector v-model="feed.period" :max-days="MAX_PERIOD_DAYS" />
            </v-col>

            <v-col cols="12" md="4" lg="6" class="d-flex justify-end align-center">
              <v-btn color="success" type="submit" :loading="feed.loading" :disabled="feed.loading">
                Показать
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-tabs-items v-model="tab" class="transparent">
      <!-- Обзор -->
      <v-tab-item>
        <v-card v-if="overview.summary" class="mt-5">
          <v-card-text>
            <v-row class="summary-row">
              <v-col cols="12" md="6" class="summary-tile">
                <div class="summary-label">Активных пользователей</div>
                <div class="summary-number">{{ overview.summary.active_clients }}</div>
              </v-col>

              <v-col cols="12" md="6" class="summary-tile">
                <div class="summary-label">Всего заходов</div>
                <div class="summary-number">{{ overview.summary.visits }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row v-if="overview.loaded" class="mt-2">
          <v-col cols="12" lg="8">
            <v-card>
              <v-card-title class="d-flex">
                Пользователи
                <v-spacer />
                <v-text-field
                  v-model="overview.search"
                  append-icon="mdi-magnify"
                  label="Поиск"
                  single-line
                  hide-details
                  dense
                  class="search-field"
                />
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="clientHeaders"
                  :items="overview.clients"
                  :search="overview.search"
                  :loading="overview.loading"
                  :page.sync="overview.page"
                  :items-per-page="CLIENTS_PER_PAGE"
                  item-key="user.id"
                  loading-text="Загрузка…"
                  no-data-text="За период никто не заходил"
                  no-results-text="Ничего не найдено"
                  hide-default-footer
                  dense
                  class="elevation-0 clickable-table"
                  @page-count="overview.pageCount = $event"
                  @click:row="openFeed"
                >
                  <template #item.last_visit_at="{ item }">
                    {{ formatDateTime(item.last_visit_at) }}
                  </template>

                  <template #item.top_sections="{ item }">
                    <v-chip
                      v-for="section in item.top_sections"
                      :key="section.title"
                      x-small
                      class="mr-1 my-1"
                    >
                      {{ section.title }} · {{ section.visits }}
                    </v-chip>
                  </template>
                </v-data-table>

                <div v-if="overview.pageCount > 1" class="d-flex mt-2">
                  <v-pagination
                    v-model="overview.page"
                    :total-visible="7"
                    :length="overview.pageCount"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card>
              <v-card-title>Что смотрят чаще всего</v-card-title>

              <v-card-text>
                <div v-if="!overview.sections.length" class="text-center grey--text">Нет данных</div>

                <div v-for="(section, index) in overview.sections" :key="section.title" class="section-row">
                  <div class="d-flex align-baseline">
                    <span class="grey--text mr-2">{{ index + 1 }}.</span>
                    <span class="font-weight-medium">{{ section.title }}</span>
                    <v-spacer />
                    <span class="text-no-wrap">
                      {{ section.visits }}
                      <span class="grey--text caption">· {{ section.clients }} кл.</span>
                    </span>
                  </div>
                  <v-progress-linear :value="sectionShare(section)" color="primary" height="6" rounded />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Лента событий -->
      <v-tab-item>
        <v-card v-if="feed.result" class="mt-5">
          <v-card-title>{{ feed.result.user.name }}</v-card-title>

          <v-card-text>
            <div v-if="!feedDays.length" class="text-center grey--text">
              За период пользователь в портал не заходил
            </div>

            <div v-for="day in feedDays" :key="day.date">
              <div class="subtitle-1 font-weight-bold mt-4">{{ day.label }}</div>

              <v-timeline dense align-top class="pt-2">
                <v-timeline-item
                  v-for="(section, index) in day.sections"
                  :key="`${section.at}-${index}`"
                  small
                  color="primary"
                >
                  <div class="d-flex">
                    <span class="feed-time font-weight-bold">{{ formatTime(section.at) }}</span>
                    <span class="font-weight-medium">{{ section.title }}</span>
                  </div>

                  <div
                    v-for="(action, actionIndex) in section.items"
                    :key="`${action.at}-${actionIndex}`"
                    class="d-flex align-center mt-1"
                  >
                    <span class="feed-time grey--text">{{ formatTime(action.at) }}</span>
                    <v-icon small class="mr-2" :color="actionIcon(action.type).color">
                      {{ actionIcon(action.type).icon }}
                    </v-icon>
                    <span>{{ action.text }}</span>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import PeriodRangeSelector from '~/components/partials/selectors/PeriodRangeSelector.vue'
import UserSelector from '~/components/partials/selectors/UserSelector.vue'

const MAX_PERIOD_DAYS = 31
const CLIENTS_PER_PAGE = 15
const TAB_OVERVIEW = 0
const TAB_FEED = 1

const ACTION_ICONS = {
  view: { icon: 'mdi-eye-outline', color: 'grey' },
  period: { icon: 'mdi-calendar-range', color: 'grey' },
  filter: { icon: 'mdi-filter-outline', color: 'grey' },
  open: { icon: 'mdi-open-in-app', color: 'accent' },
  download: { icon: 'mdi-download', color: 'accent' },
  request: { icon: 'mdi-send', color: 'success' },
  action: { icon: 'mdi-gesture-tap', color: 'grey' },
}
const DEFAULT_ACTION_ICON = { icon: 'mdi-circle-small', color: 'grey' }

function lastDays(days) {
  return [
    moment().subtract(days - 1, 'days').format('YYYY-MM-DD'),
    moment().format('YYYY-MM-DD'),
  ]
}

export default {
  name: 'PortalAnalytics',

  components: {
    PeriodRangeSelector,
    UserSelector,
  },

  data() {
    return {
      MAX_PERIOD_DAYS,
      CLIENTS_PER_PAGE,
      TAB_OVERVIEW,
      TAB_FEED,
      tab: TAB_OVERVIEW,
      overview: {
        period: lastDays(30),
        loading: false,
        loaded: false,
        search: '',
        page: 1,
        pageCount: 0,
        summary: null,
        clients: [],
        sections: [],
      },
      feed: {
        user: [],
        period: lastDays(7),
        loading: false,
        result: null,
      },
      clientHeaders: [
        { text: 'Пользователь', value: 'user.name' },
        { text: 'Последний вход', value: 'last_visit_at' },
        { text: 'Заходов', value: 'visits', align: 'end' },
        { text: 'Топ разделов', value: 'top_sections', sortable: false, filterable: false },
      ],
    }
  },

  computed: {
    maxSectionVisits() {
      return Math.max(0, ...this.overview.sections.map(section => section.visits))
    },

    feedDays() {
      if (!this.feed.result) return []

      const days = []
      this.feed.result.data.forEach(section => {
        const date = section.at.slice(0, 10)
        let day = days[days.length - 1]
        if (!day || day.date !== date) {
          day = { date, label: this.formatDay(date), sections: [] }
          days.push(day)
        }
        day.sections.push(section)
      })

      return days
    },
  },

  methods: {
    ...mapActions({
      fetchFeed: 'client_activity/fetchFeed',
      fetchSummary: 'client_activity/fetchSummary',
      fetchClients: 'client_activity/fetchClients',
      fetchSections: 'client_activity/fetchSections',
      setError: 'shared/setError',
    }),

    formatDateTime(value) {
      return value ? moment(value).format('DD.MM.YYYY HH:mm') : '—'
    },

    formatDay(date) {
      const day = moment(date)
      return `${day.format('DD.MM.YYYY')}, ${day.toDate().toLocaleDateString('ru-RU', { weekday: 'long' })}`
    },

    formatTime(value) {
      return moment(value).format('HH:mm')
    },

    actionIcon(type) {
      return ACTION_ICONS[type] || DEFAULT_ACTION_ICON
    },

    sectionShare(section) {
      return this.maxSectionVisits ? section.visits / this.maxSectionVisits * 100 : 0
    },

    showError(error) {
      this.setError((error.response && error.response.data) || { message: error })
    },

    async loadOverview() {
      if (!this.$refs.overviewForm.validate()) return

      const payload = { period: this.overview.period }
      this.overview.loading = true

      try {
        const [summary, clients, sections] = await Promise.all([
          this.fetchSummary(payload),
          this.fetchClients(payload),
          this.fetchSections(payload),
        ])
        this.overview.summary = summary
        this.overview.clients = clients
        this.overview.page = 1
        this.overview.sections = sections
        this.overview.loaded = true
      } catch (error) {
        this.showError(error)
      } finally {
        this.overview.loading = false
      }
    },

    async loadFeed() {
      if (!this.$refs.feedForm.validate()) return

      this.feed.loading = true

      try {
        this.feed.result = await this.fetchFeed({
          user_id: this.feed.user[0],
          period: this.feed.period,
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.feed.loading = false
      }
    },

    openFeed(client) {
      this.feed.user = [client.user.id]
      this.feed.period = [...this.overview.period]
      this.tab = TAB_FEED
      this.$nextTick(this.loadFeed)
    },
  },

  mounted() {
    this.loadOverview()
  },
}
</script>

<style scoped>
.summary-tile + .summary-tile {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.summary-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.summary-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.search-field {
  max-width: 260px;
}

.clickable-table >>> tbody > tr {
  cursor: pointer;
}

.section-row + .section-row {
  margin-top: 12px;
}

.feed-time {
  flex: 0 0 48px;
}
</style>
